import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Copy, Check, MessageCircle, Search } from "lucide-react";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";

const RISK_LABELS = { high: "Alto risco", medium: "Médio", ok: "Estável" };
const RISK_COLORS = {
  high: "bg-destructive/10 text-destructive border-destructive/30",
  medium: "bg-warning/10 text-warning border-warning/30",
  ok: "bg-success/10 text-success border-success/30",
};

const EMPTY_FORM = { name: "", email: "", phone: "", password: "", birthdate: "", sex: "", notes: "" };

export default function AdminPatientsPage() {
  const [patients, setPatients] = useState([]);
  const [filterRisk, setFilterRisk] = useState("");
  const [filterProtocol, setFilterProtocol] = useState("");
  const [searchText, setSearchText] = useState("");
  const [protocols, setProtocols] = useState([]);

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [createdInfo, setCreatedInfo] = useState(null); // { name, email, generated_password }
  const [copied, setCopied] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(null); // patient row
  const [deleting, setDeleting] = useState(false);
  const { perPatient: unreadPerPatient } = useUnreadMessages(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/protocols");
        setProtocols(data);
      } catch { /* noop */ }
    })();
  }, []);

  const load = async () => {
    const params = new URLSearchParams();
    if (filterRisk) params.set("risk", filterRisk);
    if (filterProtocol) params.set("protocol", filterProtocol);
    try {
      const { data } = await api.get(`/admin/patients${params.toString() ? "?" + params.toString() : ""}`);
      setPatients(data);
    } catch { /* noop */ }
  };

  useEffect(() => { load(); }, [filterRisk, filterProtocol]);

  const filteredPatients = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return patients;
    return patients.filter((p) => {
      const name = (p.name || "").toLowerCase();
      const email = (p.email || "").toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [patients, searchText]);

  const openCreate = () => { setForm(EMPTY_FORM); setCreating(true); };
  const closeCreate = () => { setCreating(false); setForm(EMPTY_FORM); };

  const submitCreate = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Nome e e-mail são obrigatórios");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
      };
      if (form.password.trim()) payload.password = form.password.trim();
      if (form.birthdate.trim()) payload.birthdate = form.birthdate.trim();
      if (form.sex.trim()) payload.sex = form.sex.trim();
      if (form.notes.trim()) payload.notes = form.notes.trim();
      const { data } = await api.post("/admin/patients", payload);
      toast.success(`Paciente ${data.name} cadastrado`);
      setCreating(false);
      setForm(EMPTY_FORM);
      if (data.generated_password) {
        setCreatedInfo({ name: data.name, email: data.email, generated_password: data.generated_password });
      }
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Não foi possível cadastrar o paciente";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const submitDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/patients/${confirmDelete.id}`);
      toast.success(`Paciente ${confirmDelete.name} removido`);
      setConfirmDelete(null);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Não foi possível excluir o paciente";
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  const copyPassword = async () => {
    if (!createdInfo?.generated_password) return;
    try {
      await navigator.clipboard.writeText(createdInfo.generated_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* noop */ }
  };

  return (
    <div className="space-y-6" data-testid="admin-patients-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-accent mb-3">Pacientes</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Acompanhamento clínico</h1>
          <p className="text-muted-foreground mt-2">{filteredPatients.length} de {patients.length} paciente(s){filterRisk || filterProtocol || searchText ? " (filtrados)" : ""}.</p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
          data-testid="add-patient-btn"
        >
          <Plus className="w-4 h-4" /> Adicionar paciente
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px] max-w-md">
          <label className="overline text-muted-foreground mb-1 block">Buscar</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Nome ou e-mail…"
              className="w-full pl-10 pr-3 py-2 rounded-sm border border-input bg-card text-sm"
              data-testid="patients-search-input"
            />
          </div>
        </div>
        <div>
          <label className="overline text-muted-foreground mb-1 block">Risco</label>
          <select value={filterRisk} onChange={(e) => setFilterRisk(e.target.value)} className="px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid="filter-risk">
            <option value="">Todos</option>
            <option value="high">Alto risco</option>
            <option value="medium">Médio</option>
            <option value="ok">Estável</option>
          </select>
        </div>
        <div>
          <label className="overline text-muted-foreground mb-1 block">Protocolo</label>
          <select value={filterProtocol} onChange={(e) => setFilterProtocol(e.target.value)} className="px-3 py-2 rounded-sm border border-input bg-card text-sm" data-testid="filter-protocol">
            <option value="">Todos</option>
            {protocols.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        {(filterRisk || filterProtocol || searchText) && (
          <button onClick={() => { setFilterRisk(""); setFilterProtocol(""); setSearchText(""); }} className="text-sm text-primary hover:underline" data-testid="clear-filters">Limpar</button>
        )}
      </div>

      <div className="bg-card border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border text-left">
            <tr>
              <th className="px-4 py-3 overline text-muted-foreground">Paciente</th>
              <th className="px-4 py-3 overline text-muted-foreground">Protocolo</th>
              <th className="px-4 py-3 overline text-muted-foreground">Status de saúde</th>
              <th className="px-4 py-3 overline text-muted-foreground">Check-ins</th>
              <th className="px-4 py-3 overline text-muted-foreground">Silêncio</th>
              <th className="px-4 py-3 overline text-muted-foreground">Risco</th>
              <th className="px-4 py-3 overline text-muted-foreground">Plano</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filteredPatients.map((p) => (
              <tr key={p.id} className="border-b border-border last:border-0 hover:bg-muted/40 transition-colors" data-testid={`admin-patient-row-${p.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="min-w-0">
                      <div className="font-medium">{p.name}</div>
                      <div className="text-xs text-muted-foreground">{p.email}</div>
                    </div>
                    {(unreadPerPatient[p.id] || 0) > 0 && (
                      <span
                        className="inline-flex items-center gap-1 min-w-[22px] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold ml-1"
                        title={`${unreadPerPatient[p.id]} mensagem(ns) do chat não lida(s)`}
                        data-testid={`patient-chat-unread-${p.id}`}
                      >
                        <MessageCircle className="w-3 h-3" />
                        {unreadPerPatient[p.id] > 9 ? "9+" : unreadPerPatient[p.id]}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{p.protocol_name || "—"}</td>
                <td className="px-4 py-3">
                  {(() => {
                    const s = p.latest_score;
                    if (s == null) {
                      return (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border text-xs border-muted-foreground/30 text-muted-foreground bg-muted" data-testid={`health-status-${p.id}`}>
                          <span className="w-2 h-2 rounded-full bg-muted-foreground/50" /> Sem check-in
                        </span>
                      );
                    }
                    const cls = s >= 75
                      ? "border-emerald-500/40 text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/10"
                      : s >= 50
                        ? "border-amber-500/40 text-amber-700 dark:text-amber-500 bg-amber-50 dark:bg-amber-900/10"
                        : "border-destructive/40 text-destructive bg-destructive/5";
                    const dot = s >= 75 ? "bg-emerald-500" : s >= 50 ? "bg-amber-500" : "bg-destructive";
                    const label = s >= 75 ? "Bom" : s >= 50 ? "Atenção" : "Crítico";
                    return (
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border text-xs font-semibold ${cls}`} data-testid={`health-status-${p.id}`}>
                        <span className={`w-2 h-2 rounded-full ${dot}`} />
                        {label} · {Math.round(s)}
                      </span>
                    );
                  })()}
                </td>
                <td className="px-4 py-3">{p.checkins_count}</td>
                <td className="px-4 py-3 text-muted-foreground">{p.days_silent >= 999 ? "—" : `${p.days_silent}d`}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 rounded-sm border text-xs ${RISK_COLORS[p.risk]}`}>
                    {RISK_LABELS[p.risk]}
                  </span>
                </td>
                <td className="px-4 py-3 capitalize text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <span>{p.subscription_plan || "—"}</span>
                    {p.subscription_status === "expired" && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-destructive border border-destructive/40 bg-destructive/5 px-1.5 py-0.5 rounded-sm" data-testid={`plan-expired-${p.id}`}>
                        Vencido
                      </span>
                    )}
                    {p.subscription_expiring_soon && p.subscription_status === "active" && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600 dark:text-amber-500 border border-amber-500/40 bg-amber-50 dark:bg-amber-900/10 px-1.5 py-0.5 rounded-sm" data-testid={`plan-expiring-${p.id}`}>
                        Vence em {p.subscription_days_remaining}d
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-3">
                    <Link
                      to={`/admin/pacientes/${p.id}`}
                      className="text-primary font-semibold hover:underline text-xs"
                      data-testid={`view-patient-${p.id}`}
                    >
                      Ver →
                    </Link>
                    <button
                      onClick={() => setConfirmDelete(p)}
                      title="Excluir paciente"
                      aria-label={`Excluir paciente ${p.name}`}
                      className="p-1.5 rounded-sm text-destructive hover:bg-destructive/10 transition-colors"
                      data-testid={`delete-patient-btn-${p.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredPatients.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-muted-foreground">Nenhum paciente encontrado.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create patient modal */}
      {creating && (
        <div
          className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4"
          onClick={closeCreate}
          data-testid="add-patient-modal"
        >
          <div
            className="bg-card border border-border rounded-sm p-6 max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="overline text-accent mb-1">Cadastro</div>
                <h3 className="brand-wordmark uppercase text-lg font-bold text-primary">Novo paciente</h3>
              </div>
              <button
                onClick={closeCreate}
                className="p-1 hover:bg-muted rounded-sm"
                data-testid="close-add-patient-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={submitCreate} className="space-y-3">
              <div>
                <label className="overline text-muted-foreground mb-1 block">Nome completo *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 rounded-sm border border-input bg-background text-sm"
                  data-testid="new-patient-name"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="overline text-muted-foreground mb-1 block">E-mail *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    className="w-full px-3 py-2 rounded-sm border border-input bg-background text-sm"
                    data-testid="new-patient-email"
                  />
                </div>
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Telefone</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-3 py-2 rounded-sm border border-input bg-background text-sm"
                    data-testid="new-patient-phone"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Data de nascimento</label>
                  <input
                    type="date"
                    value={form.birthdate}
                    onChange={(e) => setForm({ ...form, birthdate: e.target.value })}
                    className="w-full px-3 py-2 rounded-sm border border-input bg-background text-sm"
                    data-testid="new-patient-birthdate"
                  />
                </div>
                <div>
                  <label className="overline text-muted-foreground mb-1 block">Sexo biológico</label>
                  <select
                    value={form.sex}
                    onChange={(e) => setForm({ ...form, sex: e.target.value })}
                    className="w-full px-3 py-2 rounded-sm border border-input bg-background text-sm"
                    data-testid="new-patient-sex"
                  >
                    <option value="">—</option>
                    <option value="F">Feminino</option>
                    <option value="M">Masculino</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="overline text-muted-foreground mb-1 block">
                  Senha inicial <span className="text-muted-foreground/70">(deixe em branco para gerar automaticamente)</span>
                </label>
                <input
                  type="text"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  minLength={6}
                  placeholder="Mínimo 6 caracteres"
                  className="w-full px-3 py-2 rounded-sm border border-input bg-background text-sm"
                  data-testid="new-patient-password"
                />
              </div>
              <div>
                <label className="overline text-muted-foreground mb-1 block">Observações (privado)</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 rounded-sm border border-input bg-background text-sm resize-none"
                  data-testid="new-patient-notes"
                />
              </div>
              <div className="pt-2 flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={closeCreate}
                  className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted transition-colors"
                  data-testid="cancel-add-patient-btn"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60"
                  data-testid="submit-add-patient-btn"
                >
                  <Plus className="w-4 h-4" /> {saving ? "Salvando…" : "Cadastrar paciente"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password reveal modal (after auto-generation) */}
      {createdInfo && (
        <div
          className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4"
          onClick={() => setCreatedInfo(null)}
          data-testid="new-patient-password-modal"
        >
          <div
            className="bg-card border border-border rounded-sm p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="overline text-accent mb-1">Acesso criado</div>
                <h3 className="brand-wordmark uppercase text-lg font-bold text-primary">Senha temporária</h3>
              </div>
              <button
                onClick={() => setCreatedInfo(null)}
                className="p-1 hover:bg-muted rounded-sm"
                data-testid="close-new-patient-password-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              Compartilhe estes dados com <b>{createdInfo.name}</b>. A senha só será exibida agora.
            </p>
            <div className="bg-muted/40 border border-border rounded-sm p-3 mb-3">
              <div className="text-xs text-muted-foreground mb-1">E-mail</div>
              <div className="text-sm font-mono" data-testid="new-patient-created-email">{createdInfo.email}</div>
              <div className="text-xs text-muted-foreground mt-3 mb-1">Senha inicial</div>
              <div className="flex items-center gap-2">
                <div className="text-sm font-mono flex-1" data-testid="new-patient-created-password">{createdInfo.generated_password}</div>
                <button
                  onClick={copyPassword}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-border text-xs hover:bg-muted transition-colors"
                  data-testid="copy-new-patient-password-btn"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copiado" : "Copiar"}
                </button>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setCreatedInfo(null)}
                className="px-4 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
                data-testid="done-new-patient-password-btn"
              >
                Concluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div
          className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4"
          onClick={() => !deleting && setConfirmDelete(null)}
          data-testid="confirm-delete-patient-modal"
        >
          <div
            className="bg-card border border-border rounded-sm p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="overline text-destructive mb-1">Confirmar exclusão</div>
                <h3 className="brand-wordmark uppercase text-lg font-bold text-primary">Excluir paciente?</h3>
              </div>
              <button
                onClick={() => !deleting && setConfirmDelete(null)}
                className="p-1 hover:bg-muted rounded-sm"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground">
              Tem certeza que deseja excluir <b>{confirmDelete.name}</b> ({confirmDelete.email})?
              <br />
              Todos os check-ins, exames, marcadores, agendamentos e o acesso serão removidos.
              Esta ação não pode ser desfeita.
            </p>
            <div className="mt-5 flex gap-2 justify-end">
              <button
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
                className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted transition-colors disabled:opacity-60"
                data-testid="cancel-delete-patient-btn"
              >
                Cancelar
              </button>
              <button
                onClick={submitDelete}
                disabled={deleting}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-destructive text-destructive-foreground text-sm font-semibold hover:bg-destructive/90 transition-colors disabled:opacity-60"
                data-testid="confirm-delete-patient-btn"
              >
                <Trash2 className="w-4 h-4" /> {deleting ? "Excluindo…" : "Excluir paciente"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
